import { useState, useEffect } from 'react'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { useTranslation } from 'react-i18next'
import {
  CART_STORAGE_KEY,
  clearCartLines,
  PAYMENTS_ENABLED,
  serializeCartLines,
  appLegalConfig,
  DEFAULT_LOCALE,
  isAppLocale,
  localeFromPathname,
  type AppLocale,
  type AppRouteState,
  type LegalDocId,
} from '@redevicer/shared'
import type { BrowsePreset } from './components/MarketplaceHome'
import { buildAppUrl, isLegalPath, isListingPath, isOfferPath, readAppRouteFromLocation } from './utils/appUrlSync'
import MarketplaceHome from './components/MarketplaceHome'
import GuideArticlesPage from './components/GuideArticlesPage'
import SellPathWizard, { type SellEntry } from './components/SellPathWizard'
import { readPendingSellEntry } from './utils/sellListingDraft'
import { claimPendingImeiReport } from './utils/pendingImeiReport'
import ListingCatalog from './components/ListingCatalog'
import AuthPage from './components/AuthPage'
import MyListings from './components/MyListings'
import MyOffers from './components/MyOffers'
import AdminPortal from './components/admin/AdminPortal'
import ExamplesShowcase from './components/ExamplesShowcase'
import AppHeader from './components/AppHeader'
import PromoBanner from './components/PromoBanner'
import AIAssistantBubble from './components/AIAssistantBubble'
import CatalogSearchStrip from './components/CatalogSearchStrip'
import { useCatalogFilters } from './hooks/useCatalogFilters'
import { openAiAssistant } from './utils/aiAssistantEvents'
import FavoritesPage from './components/FavoritesPage'
import FavoritesGuestGate from './components/FavoritesGuestGate'
import AppFlash from './components/AppFlash'
import CartPage from './components/CartPage'
import { useCart } from './hooks/useCart'
import NotificationsPage from './components/NotificationsPage'
import ImeiReportsPage from './components/ImeiReportsPage'
import { fetchImeiReportsUnreadCount } from './utils/imeiReportsUnread'
import { releasePageScrollLock } from './utils/pageScrollLock'
import OfferDetailModal from './components/OfferDetailModal'
import AccountPage from './components/AccountPage'
import OfferChatsPage from './components/OfferChatsPage'
import ListingDetailModal from './components/ListingDetailModal'
import LegalDocumentPage from './components/LegalDocumentPage'
import SiteGateScreen from './components/SiteGateScreen'
import MobileAppOnboarding from './components/MobileAppOnboarding'
import BrandWordmark from './components/BrandWordmark'
import { mobileAppSplashMediaQuery, shouldShowMobileAppOnboarding } from './utils/isMobileWebBrowser'
import { useFavorites } from './hooks/useFavorites'
import { FavoritesAuthProvider } from './contexts/FavoritesAuthContext'
import { authAPI, listingsAPI, notificationsAPI, paymentsAPI } from './api/client'
import {
  clearPendingImeiCheckPayment,
  dispatchImeiCheckPaid,
  readPendingImeiCheckPayment,
  storePaidCheckoutSession,
} from './utils/pendingImeiCheckPayment'
import { fetchSiteGateStatus } from './api/siteGate'

type Page =
  | 'home'
  | 'buy'
  | 'sell'
  | 'examples'
  | 'myListings'
  | 'myOffers'
  | 'chats'
  | 'admin'
  | 'favorites'
  | 'cart'
  | 'notifications'
  | 'reports'
  | 'account'
  | 'guideArticles'

function readStoredUserId(): number | undefined {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return undefined
    const u = JSON.parse(raw)
    return typeof u?.id === 'number' ? u.id : undefined
  } catch {
    return undefined
  }
}

function readStoredRole(): string | undefined {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return undefined
    const u = JSON.parse(raw)
    return typeof u?.role === 'string' ? u.role : undefined
  } catch {
    return undefined
  }
}

function parseUserBrief(raw: string | null): { display: string; email?: string } {
  if (!raw) return { display: '' }
  try {
    const u = JSON.parse(raw) as { email?: string; full_name?: string }
    const display = u.full_name || u.email || ''
    return { display, email: u.email }
  } catch {
    return { display: '' }
  }
}

const BOOT_AUTH_TIMEOUT_MS = 8000

function localeForUrl(language: string): AppLocale {
  if (isAppLocale(language)) return language
  if (typeof window !== 'undefined') {
    const fromPath = localeFromPathname(window.location.pathname)
    if (fromPath) return fromPath
  }
  return DEFAULT_LOCALE
}

function stripPaymentQueryParams(language: string) {
  const params = new URLSearchParams(window.location.search)
  for (const key of ['payment', 'stripe_connect', 'session_id', 'exchange_pass']) {
    params.delete(key)
  }
  const search = params.toString() ? `?${params.toString()}` : ''
  const route = readAppRouteFromLocation(window.location.pathname, search)
  const url = buildAppUrl(route, localeForUrl(language), search)
  window.history.replaceState({ redevicerRoute: route }, '', url)
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('boot_timeout')), ms)
    promise
      .then((v) => {
        window.clearTimeout(timer)
        resolve(v)
      })
      .catch((e) => {
        window.clearTimeout(timer)
        reject(e)
      })
  })
}

function FavoritesCountSync({ onCount }: { onCount: (count: number) => void }) {
  const { count } = useFavorites()
  useEffect(() => {
    onCount(count)
  }, [count, onCount])
  return null
}

function App() {
  const { t, i18n } = useTranslation()
  const bootRoute =
    typeof window !== 'undefined' ? readAppRouteFromLocation() : ({ page: 'home' } satisfies AppRouteState)
  const [favoritesCount, setFavoritesCount] = useState(0)
  const { count: cartCount } = useCart()
  const [flashMessage, setFlashMessage] = useState<string | null>(null)

  const [page, setPage] = useState<Page>(() => bootRoute.page as Page)
  /** Set only after auth check succeeds — avoids showing member UI for stale localStorage tokens */
  const [token, setToken] = useState<string | null>(null)
  const [showAuth, setShowAuth] = useState(false)
  const [passwordResetToken, setPasswordResetToken] = useState<string | null>(() => {
    try {
      return new URLSearchParams(window.location.search).get('reset_token')
    } catch {
      return null
    }
  })
  /** After login, open listing form if user clicked Sell while logged out */
  const [postLoginIntent, setPostLoginIntent] = useState<
    'sell' | 'buy' | 'offer' | 'exchange' | 'comment' | null
  >(null)
  const [autoOfferListingId, setAutoOfferListingId] = useState<number | null>(null)
  const [autoExchangeListingId, setAutoExchangeListingId] = useState<number | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [gateLoading, setGateLoading] = useState(true)
  const [gateUnlocked, setGateUnlocked] = useState(false)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<number | undefined>(() => readStoredUserId())
  const [userRole, setUserRole] = useState<string | undefined>(() => readStoredRole())
  const catalogFiltersState = useCatalogFilters()
  const [catalogPreset, setCatalogPreset] = useState<BrowsePreset | null>(() => bootRoute.catalogPreset ?? null)
  const [catalogPresetTick, setCatalogPresetTick] = useState(0)
  const [listingDetailId, setListingDetailId] = useState<number | null>(() => bootRoute.listingId ?? null)
  const [legalDoc, setLegalDoc] = useState<LegalDocId | null>(() => bootRoute.legalDoc ?? null)
  const [accountFocusVerify, setAccountFocusVerify] = useState(() => Boolean(bootRoute.accountFocusVerify))
  const [accountFocusProfileCities, setAccountFocusProfileCities] = useState(false)
  const [offerDetailId, setOfferDetailId] = useState<number | null>(() => bootRoute.offerId ?? null)
  const [offerDetailRole, setOfferDetailRole] = useState<'seller' | 'buyer'>(() => bootRoute.offerRole ?? 'seller')
  const [notifUnread, setNotifUnread] = useState(0)
  const [reportsUnread, setReportsUnread] = useState(0)
  const [listingsCount, setListingsCount] = useState(0)
  const [userJson, setUserJson] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem('user') : null,
  )
  const [sellEntry, setSellEntry] = useState<SellEntry>(() => bootRoute.sellEntry ?? 'wizard')
  /** Bumps when user picks a sell path from header/home — remounts wizard for a clean switch. */
  const [sellNavRevision, setSellNavRevision] = useState(0)
  const [mobileAppSplashOpen, setMobileAppSplashOpen] = useState(false)

  useEffect(() => {
    if (!token) {
      setListingsCount(0)
      return
    }
    listingsAPI
      .getMyListings(token, 0, 100)
      .then((res) => setListingsCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch(() => setListingsCount(0))
  }, [token])

  useEffect(() => {
    if (!token) {
      setNotifUnread(0)
      return
    }
    function poll() {
      if (!token) return
      notificationsAPI
        .unreadCount(token)
        .then((res) => setNotifUnread(Number(res.data?.unread ?? 0)))
        .catch(() => setNotifUnread(0))
    }
    poll()
    const id = window.setInterval(poll, 15000)
    function onNotifUpdated() {
      poll()
    }
    window.addEventListener('redevicer:notifications-updated', onNotifUpdated)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('redevicer:notifications-updated', onNotifUpdated)
    }
  }, [token])

  useEffect(() => {
    if (!token || currentUserId == null) {
      setReportsUnread(0)
      return
    }
    const userId = currentUserId
    const authToken = token
    function poll() {
      void fetchImeiReportsUnreadCount(authToken, userId).then(setReportsUnread)
    }
    poll()
    const id = window.setInterval(poll, 15000)
    function onReportsUpdated() {
      poll()
    }
    window.addEventListener('redevicer:imei-reports-updated', onReportsUpdated)
    window.addEventListener('redevicer:imei-report-claimed', onReportsUpdated)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('redevicer:imei-reports-updated', onReportsUpdated)
      window.removeEventListener('redevicer:imei-report-claimed', onReportsUpdated)
    }
  }, [token, currentUserId])

  function openOfferDetail(offerId: number, role: 'seller' | 'buyer' = 'seller') {
    navigateRoute({
      page: role === 'buyer' ? 'myOffers' : 'myListings',
      listingId: null,
      offerId,
      offerRole: role,
      legalDoc: null,
      sellEntry,
      catalogPreset: null,
    })
  }

  const isGuest = !token

  const memberOnlyPages: Page[] = ['notifications', 'chats']

  function clearListingActionFlags() {
    setAutoOfferListingId(null)
    setAutoExchangeListingId(null)
  }

  function applyRouteState(route: AppRouteState) {
    setPage(route.page as Page)
    setListingDetailId(route.listingId ?? null)
    if (!(route.listingId ?? null)) clearListingActionFlags()
    setOfferDetailId(route.offerId ?? null)
    setOfferDetailRole(route.offerRole ?? 'seller')
    setLegalDoc(route.legalDoc ?? null)
    if (route.sellEntry) setSellEntry(route.sellEntry)
    setCatalogPreset(route.catalogPreset ?? null)
    setAccountFocusVerify(Boolean(route.accountFocusVerify))
    if (route.page !== 'account') setAccountFocusProfileCities(false)
  }

  function navigateRoute(next: AppRouteState, opts?: { replace?: boolean }) {
    applyRouteState(next)
    setProfileMenuOpen(false)
    const url = buildAppUrl(next, localeForUrl(i18n.language), window.location.search)
    const current = window.location.pathname + window.location.search
    if (url === current) return
    if (opts?.replace) {
      window.history.replaceState({ redevicerRoute: next }, '', url)
    } else {
      window.history.pushState({ redevicerRoute: next }, '', url)
    }
  }

  function currentRouteState(overrides: Partial<AppRouteState> = {}): AppRouteState {
    return {
      page,
      listingId: listingDetailId,
      offerId: offerDetailId,
      offerRole: offerDetailRole,
      legalDoc,
      sellEntry,
      catalogPreset,
      accountFocusVerify,
      ...overrides,
    }
  }

  function closeListingDetail() {
    if (isListingPath(window.location.pathname)) {
      window.history.back()
      return
    }
    setListingDetailId(null)
    clearListingActionFlags()
  }

  function closeOfferDetail() {
    if (isOfferPath(window.location.pathname)) {
      window.history.back()
      return
    }
    setOfferDetailId(null)
  }

  function closeLegalDocument() {
    if (isLegalPath(window.location.pathname)) {
      window.history.back()
      return
    }
    setLegalDoc(null)
  }

  function openListingDetail(id: number) {
    navigateRoute(
      currentRouteState({
        listingId: id,
        offerId: null,
        legalDoc: null,
      }),
    )
  }

  useEffect(() => {
    const route = readAppRouteFromLocation()
    const url = buildAppUrl(route, localeForUrl(i18n.language), window.location.search)
    if (window.location.pathname + window.location.search !== url) {
      window.history.replaceState({ redevicerRoute: route }, '', url)
    }
  }, [])

  useEffect(() => {
    function onPopState() {
      applyRouteState(readAppRouteFromLocation())
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => {
    if (!token && memberOnlyPages.includes(page)) {
      navigateRoute(
        currentRouteState({
          page: 'home',
          listingId: null,
          offerId: null,
          legalDoc: null,
        }),
        { replace: true },
      )
    }
  }, [token, page])

  function openLegalDocument(docId: LegalDocId) {
    navigateRoute(
      currentRouteState({
        listingId: null,
        offerId: null,
        legalDoc: docId,
      }),
    )
  }

  function goTo(next: Page, opts?: { focusSellerVerify?: boolean; replace?: boolean }) {
    if (!token && memberOnlyPages.includes(next)) {
      next = 'home'
    }
    navigateRoute(
      {
        page: next,
        listingId: null,
        offerId: null,
        legalDoc: null,
        sellEntry,
        catalogPreset: next === 'buy' ? catalogPreset : null,
        accountFocusVerify: Boolean(opts?.focusSellerVerify && next === 'account'),
      },
      { replace: opts?.replace },
    )
  }

  function scrollAppToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  /** Instant top — route changes (catalog, sell) so content is not shown mid-scroll. */
  function scrollPageToTop() {
    window.scrollTo(0, 0)
  }

  function handleLogoClick() {
    clearListingActionFlags()
    goTo('home')
    scrollAppToTop()
  }

  function openAccountForVerification() {
    goTo('account', { focusSellerVerify: true })
  }

  function openCheckHub() {
    const scrollToHub = () => {
      document.getElementById('vc-imei-hub')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    if (page !== 'home') {
      goTo('home')
      window.setTimeout(scrollToHub, 180)
    } else {
      scrollToHub()
    }
  }

  function openCatalog(preset?: BrowsePreset) {
    if (!preset) catalogFiltersState.clearAll()
    setCatalogPresetTick((x) => x + 1)
    navigateRoute({
      page: 'buy',
      listingId: null,
      offerId: null,
      legalDoc: null,
      sellEntry,
      catalogPreset: preset ?? null,
    })
    scrollPageToTop()
    requestAnimationFrame(scrollPageToTop)
  }

  useEffect(() => {
    if (page !== 'buy') return
    catalogFiltersState.applyPreset(catalogPreset)
  }, [page, catalogPresetTick, catalogPreset])

  useEffect(() => {
    if (page !== 'sell') return
    window.scrollTo(0, 0)
  }, [page, sellEntry, sellNavRevision])

  useEffect(() => {
    if (page !== 'buy') return
    scrollPageToTop()
    const id = requestAnimationFrame(scrollPageToTop)
    return () => cancelAnimationFrame(id)
  }, [page, catalogPresetTick])

  useEffect(() => {
    if (listingDetailId != null || showAuth) return
    releasePageScrollLock()
  }, [page, listingDetailId, showAuth])

  useEffect(() => {
    if (passwordResetToken) {
      setShowAuth(true)
      try {
        if (sessionStorage.getItem('redevicer:pending-sell-resume') === '1') {
          const entry = readPendingSellEntry()
          setSellEntry(entry ?? 'wizard')
          goTo('sell')
        }
      } catch {
        /* ignore */
      }
    }
  }, [passwordResetToken])

  function clearPasswordResetToken() {
    setPasswordResetToken(null)
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete('reset_token')
      const next = url.pathname + url.search + url.hash
      window.history.replaceState({}, '', next)
    } catch {
      /* ignore */
    }
  }

  function openAuth(
    intent: 'sell' | 'buy' | 'offer' | 'exchange' | 'comment' | null = null,
    listingId?: number,
  ) {
    setPostLoginIntent(intent)
    const listingIntent =
      intent === 'buy' || intent === 'offer' || intent === 'exchange' || intent === 'comment'
    if (listingIntent && listingId != null) {
      try {
        sessionStorage.removeItem('redevicer:pending-sell-resume')
        sessionStorage.setItem('redevicer:pending-auth-intent', intent)
        sessionStorage.setItem('redevicer:pending-auth-listing', String(listingId))
        if (intent === 'buy') sessionStorage.setItem('redevicer:pending-buy-listing', String(listingId))
      } catch {
        /* ignore */
      }
    }
    setShowAuth(true)
  }

  function openBuyFlow(listingId: number) {
    openListingDetail(listingId)
  }

  function requestSell() {
    setSellNavRevision((v) => v + 1)
    navigateRoute({
      page: 'sell',
      listingId: null,
      offerId: null,
      legalDoc: null,
      sellEntry: 'wizard',
      catalogPreset: null,
    })
    window.scrollTo(0, 0)
  }

  function requestSellFast() {
    setSellNavRevision((v) => v + 1)
    navigateRoute({
      page: 'sell',
      listingId: null,
      offerId: null,
      legalDoc: null,
      sellEntry: 'super-fast',
      catalogPreset: null,
    })
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        // Keep sessionStorage / cookie gate token across F5 so a refresh does not
        // look like a broken blank/password loop. New browser sessions still need
        // the password (sessionStorage + session cookie clear on browser exit).
        const s = await fetchSiteGateStatus()
        if (cancelled) return
        setGateUnlocked(!s.enabled || s.unlocked)
      } catch {
        if (!cancelled) setGateUnlocked(true)
      } finally {
        if (!cancelled) setGateLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (gateLoading || !gateUnlocked) return

    function syncSplash() {
      setMobileAppSplashOpen(shouldShowMobileAppOnboarding())
    }

    syncSplash()
    const mq = mobileAppSplashMediaQuery()
    mq?.addEventListener('change', syncSplash)
    window.addEventListener('orientationchange', syncSplash)
    return () => {
      mq?.removeEventListener('change', syncSplash)
      window.removeEventListener('orientationchange', syncSplash)
    }
  }, [gateLoading, gateUnlocked])

  useEffect(() => {
    function onGateRequired() {
      setGateUnlocked(false)
    }
    window.addEventListener('redevicer:site-gate-required', onGateRequired)
    return () => window.removeEventListener('redevicer:site-gate-required', onGateRequired)
  }, [])

  useEffect(() => {
    if (gateLoading || !gateUnlocked) {
      if (!gateLoading && !gateUnlocked) setAuthLoading(false)
      return
    }

    const savedToken = localStorage.getItem('auth_token')
    if (!savedToken) {
      setAuthLoading(false)
      return
    }

    withTimeout(authAPI.getCurrentUser(savedToken), BOOT_AUTH_TIMEOUT_MS)
      .then((res) => {
        setToken(savedToken)
        localStorage.setItem('user', JSON.stringify(res.data))
        setUserJson(JSON.stringify(res.data))
        setCurrentUserId(res.data.id)
        setUserRole(typeof res.data.role === 'string' ? res.data.role : undefined)
      })
      .catch(() => {
        setToken(null)
        setCurrentUserId(undefined)
        setUserRole(undefined)
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
        setUserJson(null)
      })
      .finally(() => setAuthLoading(false))
  }, [gateLoading, gateUnlocked])

  useEffect(() => {
    if (!gateLoading && !authLoading) return
    const timer = window.setTimeout(() => {
      setGateLoading(false)
      setAuthLoading(false)
    }, BOOT_AUTH_TIMEOUT_MS + 4000)
    return () => window.clearTimeout(timer)
  }, [gateLoading, authLoading])

  useEffect(() => {
    const q = new URLSearchParams(window.location.search)
    const pay = q.get('payment')
    const stripeConnect = q.get('stripe_connect')
    const guestListingToken = q.get('guest_listing')
    if (guestListingToken) {
      authAPI
        .redeemGuestListingLink(guestListingToken)
        .then(async (res) => {
          const accessToken = String(res.data?.access_token ?? '')
          if (!accessToken) return
          const me = await authAPI.getCurrentUser(accessToken)
          handleLogin(accessToken, me.data)
          navigateRoute(
            {
              page: 'myListings',
              listingId: null,
              offerId: null,
              legalDoc: null,
              sellEntry,
              catalogPreset: null,
            },
            { replace: true },
          )
          setFlashMessage(t('listing.guestListingLinkOk', 'Prisijungta — galite redaguoti skelbimą.'))
        })
        .catch(() => {
          setFlashMessage(t('listing.guestListingLinkFailed', 'Nuoroda nebegalioja. Prisijunkite el. paštu.'))
          setShowAuth(true)
        })
        .finally(() => {
          try {
            const params = new URLSearchParams(window.location.search)
            params.delete('guest_listing')
            const search = params.toString() ? `?${params.toString()}` : ''
            const route = readAppRouteFromLocation(window.location.pathname, search)
            window.history.replaceState(
              { redevicerRoute: route },
              '',
              buildAppUrl(route, localeForUrl(i18n.language), search),
            )
          } catch {
            /* ignore */
          }
        })
    }
    if (PAYMENTS_ENABLED && (pay === 'success' || pay === 'cancel')) {
      if (pay === 'success') {
        const sessionId = q.get('session_id')
        const saved = localStorage.getItem('auth_token')
        if (sessionId && saved && !q.get('exchange_pass')) {
          paymentsAPI.confirmCheckout(sessionId, saved).catch(() => {})
        }
        if (q.get('exchange_pass') === '1') {
          if (saved) {
            authAPI
              .getCurrentUser(saved)
              .then((res) => {
                const json = JSON.stringify(res.data)
                localStorage.setItem('user', json)
                setUserJson(json)
              })
              .catch(() => {})
          }
          navigateRoute(
            {
              page: 'account',
              listingId: null,
              offerId: null,
              legalDoc: null,
              sellEntry,
              catalogPreset: null,
            },
            { replace: true },
          )
        } else {
          localStorage.setItem(CART_STORAGE_KEY, serializeCartLines(clearCartLines()))
          window.dispatchEvent(new Event('redevicer:cart-updated'))
          navigateRoute(
            {
              page: 'myOffers',
              listingId: null,
              offerId: null,
              legalDoc: null,
              sellEntry,
              catalogPreset: null,
            },
            { replace: true },
          )
        }
      } else if (q.get('exchange_pass') === '1') {
        navigateRoute(
          {
            page: 'account',
            listingId: null,
            offerId: null,
            legalDoc: null,
            sellEntry,
            catalogPreset: null,
          },
          { replace: true },
        )
      } else {
        navigateRoute(
          {
            page: 'myOffers',
            listingId: null,
            offerId: null,
            legalDoc: null,
            sellEntry,
            catalogPreset: null,
          },
          { replace: true },
        )
      }
      stripPaymentQueryParams(i18n.language)
    } else if (PAYMENTS_ENABLED && (stripeConnect === 'return' || stripeConnect === 'refresh')) {
      const saved = localStorage.getItem('auth_token')
      if (saved) {
        navigateRoute(
          {
            page: 'account',
            listingId: null,
            offerId: null,
            legalDoc: null,
            sellEntry,
            catalogPreset: null,
          },
          { replace: true },
        )
      }
      stripPaymentQueryParams(i18n.language)
    }
  }, [])

  useEffect(() => {
    if (gateLoading || authLoading) return
    const q = new URLSearchParams(window.location.search)
    const imeiCheck = q.get('imei_check')
    if (!imeiCheck) return

    const stripImeiCheckParams = () => {
      const params = new URLSearchParams(window.location.search)
      params.delete('imei_check')
      params.delete('session_id')
      params.delete('service')
      const search = params.toString() ? `?${params.toString()}` : ''
      const route = readAppRouteFromLocation(window.location.pathname, search)
      window.history.replaceState(
        { redevicerRoute: route },
        '',
        buildAppUrl(route, localeForUrl(i18n.language), search),
      )
    }

    if (imeiCheck === 'cancel') {
      clearPendingImeiCheckPayment()
      stripImeiCheckParams()
      return
    }

    if (imeiCheck === 'success') {
      const sessionId = q.get('session_id')
      const service = q.get('service')
      const pending = readPendingImeiCheckPayment()
      const saved = localStorage.getItem('auth_token')

      void (async () => {
        if (sessionId && saved) {
          try {
            await paymentsAPI.confirmCheckout(sessionId, saved)
          } catch {
            /* check endpoint also syncs Stripe session */
          }
        }
        if (sessionId) {
          const svc = (service?.trim() || pending?.service || '').toLowerCase()
          const imei = pending?.imei
          if (imei && svc) {
            dispatchImeiCheckPaid({
              imei,
              service: svc,
              checkoutSessionId: sessionId,
            })
            clearPendingImeiCheckPayment()
          } else if (svc && sessionId.startsWith('cs_')) {
            storePaidCheckoutSession(sessionId, svc)
            setFlashMessage(t('vc.imeiHub.result.paymentReturnReenterImei'))
          } else {
            setFlashMessage(t('vc.imeiHub.result.paymentReturnReenterImei'))
          }
        }
        stripImeiCheckParams()
        navigateRoute(
          {
            page: 'home',
            listingId: null,
            offerId: null,
            legalDoc: null,
            sellEntry,
            catalogPreset: null,
          },
          { replace: true },
        )
        window.setTimeout(() => {
          document.getElementById('vc-imei-hub')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 350)
      })()
    }
  }, [gateLoading, authLoading, i18n.language, sellEntry])

  useEffect(() => {
    function onSessionExpired() {
      handleLogout()
      setShowAuth(true)
    }
    window.addEventListener('redevicer:session-expired', onSessionExpired)
    return () => window.removeEventListener('redevicer:session-expired', onSessionExpired)
  }, [])

  function handleLogin(newToken: string, user: any) {
    setToken(newToken)
    localStorage.setItem('auth_token', newToken)
    localStorage.setItem('user', JSON.stringify(user))
    setUserJson(JSON.stringify(user))
    setCurrentUserId(typeof user?.id === 'number' ? user.id : undefined)
    setUserRole(typeof user?.role === 'string' ? user.role : undefined)
    void claimPendingImeiReport(newToken)
    let resumeSellDraft = false
    try {
      resumeSellDraft = sessionStorage.getItem('redevicer:pending-sell-resume') === '1'
    } catch {
      /* ignore */
    }

    const listingIntent =
      postLoginIntent === 'buy' ||
      postLoginIntent === 'offer' ||
      postLoginIntent === 'exchange' ||
      postLoginIntent === 'comment'

    if (listingIntent) {
      const intent = postLoginIntent
      let raw: string | null = null
      try {
        raw =
          sessionStorage.getItem('redevicer:pending-auth-listing') ||
          sessionStorage.getItem('redevicer:pending-buy-listing')
        sessionStorage.removeItem('redevicer:pending-auth-listing')
        sessionStorage.removeItem('redevicer:pending-auth-intent')
        sessionStorage.removeItem('redevicer:pending-buy-listing')
      } catch {
        /* ignore */
      }
      const id = raw ? parseInt(raw, 10) : NaN
      setPostLoginIntent(null)
      if (Number.isFinite(id)) {
        if (intent === 'offer') setAutoOfferListingId(id)
        if (intent === 'exchange') setAutoExchangeListingId(id)
        navigateRoute({
          page: 'buy',
          listingId: id,
          offerId: null,
          legalDoc: null,
          sellEntry,
          catalogPreset: null,
        })
      }
      return
    }

    if (resumeSellDraft || postLoginIntent === 'sell') {
      setPostLoginIntent(null)
      const entry = readPendingSellEntry()
      navigateRoute({
        page: 'sell',
        listingId: null,
        offerId: null,
        legalDoc: null,
        sellEntry: entry ?? 'wizard',
        catalogPreset: null,
      })
      return
    }
    setPostLoginIntent(null)
  }

  function handleLogout() {
    setToken(null)
    setCurrentUserId(undefined)
    setUserRole(undefined)
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    setUserJson(null)
    navigateRoute(
      {
        page: 'home',
        listingId: null,
        offerId: null,
        legalDoc: null,
        sellEntry: 'wizard',
        catalogPreset: null,
      },
      { replace: true },
    )
  }

  function handleAdminLogout() {
    setToken(null)
    setCurrentUserId(undefined)
    setUserRole(undefined)
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    setUserJson(null)
    navigateRoute(
      {
        page: 'admin',
        listingId: null,
        offerId: null,
        legalDoc: null,
        sellEntry: 'wizard',
        catalogPreset: null,
      },
      { replace: true },
    )
  }

  function refreshUserJson(data: unknown) {
    const json = JSON.stringify(data)
    localStorage.setItem('user', json)
    setUserJson(json)
  }

  /** Direct /legal/* links must work without waiting on gate/auth (public documents). */
  if (legalDoc && (gateLoading || authLoading || !gateUnlocked)) {
    return (
      <div className="app-shell vc-app-shell vc-app-shell--legal-public">
        <LegalDocumentPage docId={legalDoc} onBack={closeLegalDocument} />
      </div>
    )
  }

  if (gateLoading || authLoading) {
    return (
      <div className="app-boot auth-loading" role="status" aria-live="polite">
        <p className="app-boot__brand">reDevicer</p>
        <p className="app-boot__text">{t('common.loading')}</p>
      </div>
    )
  }

  if (!gateUnlocked) {
    return (
      <SiteGateScreen onUnlocked={() => setGateUnlocked(true)} />
    )
  }

  const { display: profileDisplayName, email: profileEmail } = parseUserBrief(userJson)

  const profileName = profileDisplayName || t('vc.account.guest')
  const googleClientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim() || undefined

  if (page === 'admin') {
    return (
      <AdminPortal
        token={token}
        userRole={userRole}
        userEmail={profileEmail}
        onLogin={handleLogin}
        onLogout={handleAdminLogout}
        onGoToSite={() => goTo('home')}
      />
    )
  }

  const authOverlay = (
    <AuthPage
      resetToken={passwordResetToken}
      onResetComplete={clearPasswordResetToken}
      onCancel={() => {
        setShowAuth(false)
        setPostLoginIntent(null)
        clearPasswordResetToken()
      }}
      onLogin={(newToken, user) => {
        handleLogin(newToken, user)
        setShowAuth(false)
        clearPasswordResetToken()
      }}
    />
  )

  const requireLoginForFavorites = () => {
    setFlashMessage(t('vc.favorites.loginToSave'))
  }

  return (
    <FavoritesAuthProvider loggedIn={!!token} onRequireLogin={requireLoginForFavorites}>
      <FavoritesCountSync onCount={setFavoritesCount} />
      <AppFlash message={flashMessage} onDismiss={() => setFlashMessage(null)} />
      {mobileAppSplashOpen ? (
        <MobileAppOnboarding onDismiss={() => setMobileAppSplashOpen(false)} />
      ) : null}
      {showAuth &&
        (googleClientId ? (
          <GoogleOAuthProvider clientId={googleClientId}>{authOverlay}</GoogleOAuthProvider>
        ) : (
          authOverlay
        ))}

      <ListingDetailModal
        listingId={listingDetailId}
        onClose={closeListingDetail}
        token={token}
        currentUserId={currentUserId}
        onRequireLogin={(intent = 'buy') => {
          if (listingDetailId != null) openAuth(intent, listingDetailId)
          else openAuth(null)
        }}
        autoOfferNow={autoOfferListingId != null && listingDetailId === autoOfferListingId}
        autoExchangeNow={autoExchangeListingId != null && listingDetailId === autoExchangeListingId}
        onBuyComplete={() => {
          clearListingActionFlags()
          goTo('myOffers')
        }}
        onGoToAccount={() => goTo('account')}
      />

      {token && (
        <OfferDetailModal
          offerId={offerDetailId}
          token={token}
          role={offerDetailRole}
          onClose={closeOfferDetail}
          onUpdated={() => {
            notificationsAPI.unreadCount(token).then((res) => setNotifUnread(Number(res.data?.unread ?? 0)))
          }}
          onGoToDeals={() => goTo('myOffers')}
        />
      )}

      <div className={`app-shell vc-app-shell${page === 'sell' ? ' vc-app-shell--sell' : ''}`}>
        {page !== 'sell' ? (
          <PromoBanner onAction={requestSell} onOpenCatalog={() => openCatalog()} />
        ) : null}
        <AppHeader
          onLogo={handleLogoClick}
          onBuy={() => openCatalog()}
          onCheck={openCheckHub}
          onSell={requestSell}
          onSellFast={requestSellFast}
          isLoggedIn={!!token}
          onOpenFavorites={() => goTo('favorites')}
          {...(PAYMENTS_ENABLED
            ? { onOpenCart: () => goTo('cart'), cartCount }
            : { onOpenCart: () => {}, cartCount: 0, showCart: false })}
          onOpenNotifications={() => goTo('notifications')}
          onOpenReports={() => goTo('reports')}
          notificationsUnreadCount={notifUnread}
          reportsUnreadCount={reportsUnread}
          onOpenAccount={() => (token ? goTo('account') : openAuth(null))}
          favoritesCount={favoritesCount}
          profileMenuOpen={profileMenuOpen}
          setProfileMenuOpen={setProfileMenuOpen}
          profileDisplayName={profileName}
          profileEmail={profileEmail}
          isAdmin={userRole === 'admin'}
          onSeeProfile={() => goTo('account')}
          onProfileMyListings={() => goTo('myListings')}
          onProfileMyOffers={() => goTo('myOffers')}
          onProfileMyChats={() => goTo('chats')}
          onProfileMyReports={() => goTo('reports')}
          onProfileExamples={() => goTo('examples')}
          onProfileAdmin={userRole === 'admin' ? () => goTo('admin') : undefined}
          onProfileLogout={handleLogout}
          onProfileLegal={openLegalDocument}
          isGuest={isGuest}
          onGuestLogin={() => openAuth(null)}
          language={i18n.language}
          onLanguage={(lng) => i18n.changeLanguage(lng)}
        />
        {page === 'buy' ? (
          <div className="vc-search-below-strip vc-search-below-strip--catalog">
            <CatalogSearchStrip
              filters={catalogFiltersState.filters}
              onPatch={catalogFiltersState.patch}
              onClearAll={() => {
                catalogFiltersState.clearAll()
                setCatalogPreset(null)
              }}
              hasActiveFilters={catalogFiltersState.hasActiveFilters}
            />
          </div>
        ) : null}
        <main
          className={`app-main vc-main${page === 'home' ? ' app-main--home' : ''}${page === 'sell' ? ' app-main--sell' : ''}${page === 'buy' ? ' app-main--catalog' : ''}`}
        >
          {legalDoc ? (
            <LegalDocumentPage docId={legalDoc} onBack={closeLegalDocument} />
          ) : (
            <>
          {page === 'home' && (
            <MarketplaceHome
              onOpenCatalog={openCatalog}
              onSell={requestSell}
              onSellFast={requestSellFast}
              onOpenListingDetail={openListingDetail}
              onOpenAi={() => openAiAssistant()}
              token={token}
              userPreview={
                userJson
                  ? (() => {
                      try {
                        return JSON.parse(userJson) as { email?: string; full_name?: string }
                      } catch {
                        return null
                      }
                    })()
                  : null
              }
              currentUserId={currentUserId}
              onRequireLogin={(intent, listingId) => openAuth(intent ?? null, listingId)}
              onGuestAuthed={handleLogin}
              onListingCreated={() => goTo('myListings')}
              onFlashMessage={setFlashMessage}
              onBuyNow={openBuyFlow}
              onViewAllArticles={() => goTo('guideArticles')}
            />
          )}
          {page === 'guideArticles' && <GuideArticlesPage onBack={() => goTo('home')} />}
          {page === 'examples' && <ExamplesShowcase onOpenCatalog={() => openCatalog()} />}
          {page === 'buy' && (
            <ListingCatalog
              token={token}
              currentUserId={currentUserId}
              catalogPreset={catalogPreset}
              catalogPresetTick={catalogPresetTick}
              filters={catalogFiltersState.filters}
              onFiltersPatch={catalogFiltersState.patch}
              onClearFilters={() => {
                catalogFiltersState.clearAll()
                setCatalogPreset(null)
              }}
              hasActiveFilters={catalogFiltersState.hasActiveFilters}
              onClearCatalogPreset={() => setCatalogPreset(null)}
              onRequireLogin={(intent, listingId) => openAuth(intent ?? null, listingId)}
              onOpenListingDetail={openListingDetail}
              onBuyNow={openBuyFlow}
              onGoToAccount={() => goTo('account')}
            />
          )}
          {page === 'sell' && (
            <div className="app-page app-page--full listing-sell-page">
              <SellPathWizard
                key={`${sellEntry}-${sellNavRevision}`}
                entry={sellEntry}
                token={token}
                userPreview={
                  userJson
                    ? (() => {
                        try {
                          return JSON.parse(userJson) as { email?: string; full_name?: string }
                        } catch {
                          return null
                        }
                      })()
                    : null
                }
                onGuestAuthed={handleLogin}
                onCreated={() => goTo('myListings')}
                onFlashMessage={setFlashMessage}
                onOpenAccountSettings={() => {
                  if (token) {
                    setAccountFocusProfileCities(true)
                    goTo('account')
                  } else {
                    openAuth(null)
                  }
                }}
              />
            </div>
          )}
          {page === 'myListings' && token && (
            <MyListings token={token} onOpenOfferDetail={(id) => openOfferDetail(id, 'seller')} />
          )}
          {page === 'myOffers' && token && (
            <MyOffers
              token={token}
              onOpenOfferDetail={(id) => openOfferDetail(id, 'buyer')}
            />
          )}
          {page === 'chats' && token && (
            <div className="app-page app-page--narrow">
              <OfferChatsPage
                token={token}
                onOpenConversation={(id, role) => openOfferDetail(id, role)}
              />
            </div>
          )}
          {page === 'favorites' && !token && (
            <div className="app-page app-page--narrow">
              <FavoritesGuestGate onLogin={() => openAuth(null)} onBrowse={() => openCatalog()} />
            </div>
          )}
          {page === 'favorites' && token && (
            <div className="app-page app-page--narrow">
              <FavoritesPage
                token={token}
                currentUserId={currentUserId}
                onBuyNow={openBuyFlow}
                onOpenListingDetail={openListingDetail}
                onBrowse={() => openCatalog()}
              />
            </div>
          )}
          {PAYMENTS_ENABLED && page === 'cart' && (
            <div className="app-page app-page--narrow">
              <CartPage
                token={token}
                currentUserId={currentUserId}
                onOpenListing={openListingDetail}
                onRequireLogin={() => openAuth(null)}
                onBrowse={() => openCatalog()}
              />
            </div>
          )}
          {page === 'notifications' && token && (
            <div className="app-page app-page--narrow">
              <NotificationsPage
                token={token}
                onUnreadChange={setNotifUnread}
                onOpenOffer={(id, role) => {
                  openOfferDetail(id, role)
                  if (role === 'seller') goTo('myListings')
                }}
                onOpenMyOffers={() => goTo('myOffers')}
                onOpenListing={openListingDetail}
              />
            </div>
          )}
          {page === 'reports' && token && currentUserId != null && (
            <div className="app-page app-page--narrow">
              <ImeiReportsPage
                token={token}
                userId={currentUserId}
                onBrowseHub={() => goTo('home')}
                onUnreadChange={setReportsUnread}
              />
            </div>
          )}
          {page === 'account' && (
            <AccountPage
              token={token}
              userJson={userJson}
              isAdmin={userRole === 'admin'}
              onBrowse={() => openCatalog()}
              onExamples={() => goTo('examples')}
              onMyListings={() => goTo('myListings')}
              onMyOffers={() => goTo('myOffers')}
              onMyChats={() => goTo('chats')}
              onMyReports={() => goTo('reports')}
              onAdmin={() => goTo('admin')}
              onLogout={handleLogout}
              onLogin={() => openAuth(null)}
              onSell={requestSell}
              language={i18n.language}
              onLanguage={(lng) => i18n.changeLanguage(lng)}
              onOpenLegal={openLegalDocument}
              focusSellerVerify={accountFocusVerify}
              focusProfileCities={accountFocusProfileCities}
              onFocusProfileCitiesHandled={() => setAccountFocusProfileCities(false)}
              listingsCount={listingsCount}
              onUserUpdated={refreshUserJson}
            />
          )}
            </>
          )}
        </main>
        <footer className="site-footer">
          <div className="site-footer__inner">
            <div className="site-footer__top">
              <div className="site-footer__about">
                <BrandWordmark className="brand-wordmark--footer" title="" />
                <p className="site-footer__tagline">{t('vc.headerTagline')}</p>
                <p className="site-footer__desc">{t('footer.aboutDesc')}</p>
                <div className="site-footer__social">
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="site-footer__social-btn" aria-label="Facebook">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                  </a>
                  <a href="https://www.instagram.com/redevicer" target="_blank" rel="noopener noreferrer" className="site-footer__social-btn" aria-label="Instagram">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/></svg>
                  </a>
                </div>
              </div>

              <div className="site-footer__columns">
                <div className="site-footer__col-stack">
                  <div className="site-footer__contact">
                    <h4>{t('footer.contactHeading')}</h4>
                    <p>
                      <strong>{t('footer.emailLabel')}</strong> {appLegalConfig.infoEmail}
                    </p>
                  </div>

                  <div className="site-footer__rekvizitai">
                    <h4>{t('footer.companyHeading')}</h4>
                    <p><strong>{appLegalConfig.legalEntityName}</strong></p>
                    <p>{t('footer.companyCode', { code: appLegalConfig.companyCode })}</p>
                    {appLegalConfig.vatCode ? <p>{t('footer.vatCode', { code: appLegalConfig.vatCode })}</p> : null}
                    {appLegalConfig.bankName && appLegalConfig.iban ? (
                      <>
                        <p>{t('footer.bankName', { name: appLegalConfig.bankName })}</p>
                        <p>{t('footer.iban', { iban: appLegalConfig.iban })}</p>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="site-footer__col-stack">
                  <div className="site-footer__nav">
                    <h4>{t('footer.navHeading')}</h4>
                    <button type="button" onClick={() => goTo('home')}>{t('vc.tab.home')}</button>
                    <button type="button" onClick={() => openCatalog()}>{t('catalog.title')}</button>
                    <button type="button" onClick={requestSell}>{t('vc.headerSell')}</button>
                  </div>

                  <div className="site-footer__legal">
                    <h4>{t('footer.legalHeading')}</h4>
                    <button type="button" onClick={() => openLegalDocument('privacy')}>{t('legal.doc.privacy')}</button>
                    <button type="button" onClick={() => openLegalDocument('terms')}>{t('legal.doc.terms')}</button>
                    <button type="button" className="site-footer__legal-buyer" onClick={() => openLegalDocument('buyer_protection')}>
                      {t('legal.doc.buyer_protection')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="site-footer__bottom">
              <p className="site-footer__copy">© {new Date().getFullYear()} {appLegalConfig.legalEntityName}. {t('footer.rights')}</p>
            </div>
          </div>
        </footer>
      </div>
      <AIAssistantBubble token={token} />
    </FavoritesAuthProvider>
  )
}

export default App
